import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chiama } from './cliente.js';

export type TipoAnagrafica = 'customer' | 'supplier' | 'agent' | 'lead' | 'prospect' | 'operator';

export interface RigaAnagrafica {
  id: number;
  name: string;
  type: string;
  bcCodeSpa: string | null;
  bcCodeInc: string | null;
  taxNumber: string | null;
  city: string | null;
  country: string | null;
  /** L unica cosa che l elenco deve dire: non e la sede, e una destinazione. */
  otherLocation: boolean;
  sectional: string | null;
  currency: string | null;
  archived: boolean;
}

export interface Ricerca {
  page: number;
  size: number;
  sort: string;
  dir: 'asc' | 'desc';
  q: string;
  archived: boolean;
  /** I filtri della ricerca avanzata, quelli impostati. */
  filters: Record<string, string | number | undefined>;
}

export const RICERCA_INIZIALE: Ricerca = {
  page: 1,
  size: 50,
  sort: 'name',
  dir: 'asc',
  q: '',
  archived: false,
  filters: {},
};

interface Pagina {
  rows: RigaAnagrafica[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

function queryString(tipo: TipoAnagrafica, r: Ricerca, extra: Record<string, string> = {}) {
  return new URLSearchParams({
    type: tipo,
    page: String(r.page),
    size: String(r.size),
    sort: r.sort,
    dir: r.dir,
    ...(r.q ? { q: r.q } : {}),
    ...(r.archived ? { archived: 'true' } : {}),
    ...Object.fromEntries(
      Object.entries(r.filters ?? {})
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ),
    ...extra,
  }).toString();
}

export function useAnagrafiche(tipo: TipoAnagrafica, ricerca: Ricerca) {
  return useQuery({
    queryKey: ['anagrafiche', tipo, ricerca],
    queryFn: () => chiama<Pagina>(`/api/identities?${queryString(tipo, ricerca)}`),
    // la pagina precedente resta a video mentre arriva la nuova: senza,
    // la tabella sfarfalla a ogni tasto premuto nella ricerca
    placeholderData: (precedente) => precedente,
  });
}

/**
 * L'esportazione non passa da fetch: è il browser a scaricare il file, e il
 * cookie di sessione viaggia da solo. Si esporta quello che è stato
 * filtrato, non la pagina a video.
 */
export function esporta(tipo: TipoAnagrafica, ricerca: Ricerca, formato: 'xlsx' | 'csv'): void {
  // il nome del parametro è quello che legge il servizio: `format`, non
  // `formato` — scritto male, il CSV scaricava un Excel senza dirlo
  window.location.href = `/api/identities/export?${queryString(tipo, ricerca, { format: formato })}`;
}

export interface DefinizioneFiltri {
  filters: {
    key: string;
    label: string;
    type: 'text' | 'list' | 'yesNo' | 'choice';
    options?: string;
    values?: { value: string; label: string }[];
  }[];
  options: Record<string, { id: string | number; label: string }[]>;
}

export function useDefinizioneFiltri() {
  return useQuery({
    queryKey: ['filtri-anagrafiche'],
    queryFn: () => chiama<DefinizioneFiltri>('/api/identities/filters'),
    // le liste cambiano di rado: non ha senso rileggerle a ogni schermata
    staleTime: 10 * 60_000,
  });
}

export interface Vista {
  id: number;
  scope: string;
  name: string;
  definition: Record<string, unknown>;
  shared: boolean;
  mine: boolean;
}

export function useViste(scope: string) {
  return useQuery({
    queryKey: ['viste', scope],
    queryFn: () => chiama<{ views: Vista[] }>(`/api/views?scope=${encodeURIComponent(scope)}`),
  });
}

export function useSalvaVista(scope: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: {
      name: string;
      definition: Record<string, unknown>;
      condivisa?: boolean;
    }) =>
      chiama<{ id: number; name: string }>('/api/views', {
        metodo: 'POST',
        corpo: { scope, ...dati },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['viste', scope] }),
  });
}

export function useCancellaVista(scope: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      chiama<{ deleted: boolean }>(`/api/views/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['viste', scope] }),
  });
}
